// Rendered process diagrams for the listing roadmap — inline SVG, no dependency,
// drawn from live data so they never drift from the model. Three pieces:
//   • PhaseFlowDiagram  — the 20-phase critical-path flow (spine + nodes + gates)
//   • LineageArcDiagram — producers→consumers arcs per shared data element
//   • FlowLegend        — a small static "how to read this" key (also used in Help)
import { Link } from "react-router-dom";
import type { PhaseDto, StepDto, ProcessGateDto, DataElementDto, LineageEdgeDto } from "@arcline/shared";
import { c, font } from "../lib/theme";

// "Ph.4" / "Ph. 4" / "Phase 4" → 4
function gatePhaseNum(ref: string | null): number | null {
  const m = (ref ?? "").match(/(\d+)/);
  return m ? Number(m[1]) : null;
}

const ROW = 74; // px per phase row — shared by the SVG rail and the HTML card

export function PhaseFlowDiagram({ phases, gates }: { phases: PhaseDto[]; gates: ProcessGateDto[] }) {
  const gatesByPos = new Map<number, ProcessGateDto[]>();
  for (const g of gates) {
    const n = gatePhaseNum(g.phaseRef);
    if (n == null) continue;
    const arr = gatesByPos.get(n) ?? [];
    arr.push(g); gatesByPos.set(n, arr);
  }
  return (
    <div>
      {phases.map((p, i) => {
        const cp = p.onCriticalPath;
        const phaseGates = gatesByPos.get(p.position) ?? [];
        const hasGate = phaseGates.length > 0;
        const last = i === phases.length - 1;
        return (
          <div key={p.id} data-keep-cols style={{ display: "grid", gridTemplateColumns: `60px 1fr`, minHeight: ROW }}>
            {/* SVG rail cell — node + connector; stacks into a continuous spine */}
            <svg width={60} height={ROW} style={{ display: "block" }} aria-hidden>
              {!last && <line x1={30} y1={ROW / 2} x2={30} y2={ROW} stroke={cp ? c.red : c.line2} strokeWidth={cp ? 3 : 1.5} />}
              {i > 0 && <line x1={30} y1={0} x2={30} y2={ROW / 2} stroke={phases[i - 1].onCriticalPath || cp ? c.red : c.line2} strokeWidth={phases[i - 1].onCriticalPath || cp ? 3 : 1.5} />}
              {hasGate ? (
                <g transform={`translate(30 ${ROW / 2}) rotate(45)`}>
                  <rect x={-9} y={-9} width={18} height={18} rx={2} fill={c.redHi} stroke="#fff" strokeWidth={1.5} />
                </g>
              ) : (
                <circle cx={30} cy={ROW / 2} r={9} fill={cp ? c.red : c.panel} stroke={cp ? "#fff" : c.blueHi} strokeWidth={2} />
              )}
              <text x={30} y={ROW / 2 + 3.5} textAnchor="middle" fontSize={10.5} fontFamily={font.mono} fontWeight={700} fill={hasGate || cp ? "#fff" : c.blueHi}>{p.position}</text>
            </svg>

            {/* phase card */}
            <div style={{ padding: "8px 0 8px 6px" }}>
              <div style={{ background: cp ? "rgba(206,17,38,0.06)" : c.panel2, border: `1px solid ${cp ? c.red + "55" : c.line}`, borderRadius: 8, padding: "9px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 15, color: c.textHi, fontWeight: 600 }}>{p.name}</span>
                  {cp && <span style={badge(c.redHi)}>CRITICAL PATH</span>}
                  <span style={{ marginLeft: "auto", fontSize: 12.5, color: c.muted }}>{p.timing}</span>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 5, font: `500 13px ${font.sans}`, color: c.muted2, flexWrap: "wrap" }}>
                  <span>{p.stepCount} steps</span>
                  <span style={{ color: c.cyan }}>▼ {p.intakeCount} intake</span>
                  <span style={{ color: c.green }}>▲ {p.productionCount} production</span>
                </div>
                {phaseGates.map((g) => (
                  <div key={g.id} style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ color: c.redHi, fontSize: 13 }}>⟐</span>
                    <span style={{ font: `700 12px ${font.mono}`, color: c.redHi }}>{g.key}</span>
                    <span style={{ fontSize: 12.5, color: c.muted2 }}>{g.name.replace(/^CP-\d+\s*/, "")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LineageArcDiagram({ dataElements, edges }: { dataElements: DataElementDto[]; edges: LineageEdgeDto[] }) {
  const W = 940, BAND = 104, LABEL = 188, PAD = 26;
  const maxStep = 175;
  const x = (s: number): number => LABEL + (Math.max(1, Math.min(maxStep, s)) / maxStep) * (W - LABEL - PAD);
  const height = Math.max(1, dataElements.length) * BAND;

  return (
    <svg viewBox={`0 0 ${W} ${height}`} width="100%" style={{ display: "block" }} role="img" aria-label="Data lineage arc diagram">
      {dataElements.map((de, k) => {
        const y0 = k * BAND;
        const axisY = y0 + BAND - 30;
        const deEdges = edges.filter((e) => e.dataElement === de.key);
        const name = de.name.length > 30 ? de.name.slice(0, 30) + "…" : de.name;
        return (
          <g key={de.id}>
            {/* band separator */}
            {k > 0 && <line x1={0} y1={y0} x2={W} y2={y0} stroke={c.line} strokeWidth={1} />}
            {/* label */}
            <text x={8} y={y0 + 26} fontSize={12.5} fontFamily={font.sans} fontWeight={600} fill={c.textHi}>{name}</text>
            <text x={8} y={y0 + 42} fontSize={11} fontFamily={font.mono} fill={c.muted}>
              <tspan fill={c.green}>▲{de.producedBySteps.length}</tspan> <tspan fill={c.amber}>▼{de.consumedBySteps.length}</tspan> <tspan>· {deEdges.length} edges</tspan>
            </text>
            {/* axis */}
            <line x1={x(1)} y1={axisY} x2={x(maxStep)} y2={axisY} stroke={c.line2} strokeWidth={1} />
            {[1, 50, 100, 150, 175].map((t) => (
              <g key={t}>
                <line x1={x(t)} y1={axisY - 3} x2={x(t)} y2={axisY + 3} stroke={c.muted} strokeWidth={1} />
                <text x={x(t)} y={axisY + 15} textAnchor="middle" fontSize={11} fontFamily={font.mono} fill={c.muted}>{t}</text>
              </g>
            ))}
            {/* arcs producers→consumers (arch above the axis) */}
            {deEdges.map((e) => {
              const x1 = x(e.fromStepNo), x2 = x(e.toStepNo);
              const span = Math.abs(x2 - x1);
              const h = Math.min(axisY - y0 - 10, 14 + span * 0.28);
              return <path key={e.id} d={`M ${x1} ${axisY} Q ${(x1 + x2) / 2} ${axisY - h} ${x2} ${axisY}`} fill="none" stroke={c.blueHi} strokeWidth={1} opacity={0.32} />;
            })}
            {/* producer / consumer dots */}
            {de.producedBySteps.map((s) => <circle key={"p" + s} cx={x(s)} cy={axisY} r={3.4} fill={c.green} />)}
            {de.consumedBySteps.map((s) => <circle key={"c" + s} cx={x(s)} cy={axisY} r={3} fill={c.amber} />)}
          </g>
        );
      })}
    </svg>
  );
}

// Whether a step yields any artifact (document or data element). Steps that don't
// are "action" steps — still part of the logical flow, just with no produced output.
function stepHasArtifact(s: StepDto): boolean {
  return s.intake.length > 0 || s.production.length > 0 || s.produces.length > 0 || s.consumes.length > 0;
}

// Logical step-flow — every step grouped by phase as a connected flow guide.
// Deliberately includes "action" steps that produce no data/documents, so it
// reads as the whole logical sequence, not only the data-producing parts.
export function StepFlowDiagram({ steps, phases }: { steps: StepDto[]; phases: PhaseDto[] }) {
  const byPhase = new Map<string, StepDto[]>();
  for (const s of steps) {
    const arr = byPhase.get(s.phaseKey) ?? [];
    arr.push(s); byPhase.set(s.phaseKey, arr);
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {phases.map((p, pi) => {
        const ps = (byPhase.get(p.key) ?? []).slice().sort((a, b) => a.stepNo - b.stepNo);
        return (
          <div key={p.id}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, margin: "6px 0 8px" }}>
              <span style={{ font: `700 12px ${font.mono}`, color: p.onCriticalPath ? c.redHi : c.blueHi, background: c.navItem, border: `1px solid ${(p.onCriticalPath ? c.red : c.blueHi)}55`, borderRadius: 5, padding: "3px 7px" }}>P{String(p.position).padStart(2, "0")}</span>
              <span style={{ fontSize: 14.5, color: c.textHi, fontWeight: 600 }}>{p.name}</span>
              {p.onCriticalPath && <span style={badge(c.redHi)}>CRITICAL PATH</span>}
              <span style={{ marginLeft: "auto", fontSize: 12, color: c.muted }}>{ps.length} steps</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5, paddingLeft: 8, borderLeft: `2px solid ${p.onCriticalPath ? c.red + "55" : c.line}`, paddingBottom: 6 }}>
              {ps.map((s, si) => {
                const cp = s.onCriticalPath;
                const artifact = stepHasArtifact(s);
                const bd = cp ? c.redHi : artifact ? c.blueHi : c.line2;
                return (
                  <span key={s.stepNo} style={{ display: "inline-flex", alignItems: "center" }}>
                    <Link to="/listing/roadmap" title={`#${s.stepNo} · ${s.name}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none",
                        background: cp ? "rgba(206,17,38,0.10)" : artifact ? c.panel2 : "transparent",
                        border: `1px ${artifact || cp ? "solid" : "dashed"} ${cp || artifact ? bd : bd + "88"}`,
                        borderRadius: 6, padding: "5px 9px" }}>
                      <span style={{ font: `700 12px ${font.mono}`, color: bd }}>{s.stepNo}</span>
                      <span style={{ fontSize: 13, color: artifact || cp ? c.text : c.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 188 }}>
                        {s.name.replace(/\.$/, "")}
                      </span>
                    </Link>
                    {si < ps.length - 1 && <span style={{ color: c.muted, margin: "0 1px", fontSize: 13 }}>→</span>}
                  </span>
                );
              })}
            </div>
            {pi < phases.length - 1 && <div style={{ textAlign: "center", color: c.muted, fontSize: 14, margin: "1px 0" }}>↓</div>}
          </div>
        );
      })}
    </div>
  );
}

export function StepFlowLegend() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", alignItems: "center", font: `500 13px ${font.sans}`, color: c.muted2 }}>
      <Key swatch={<rect x={2} y={5} width={14} height={9} rx={2} fill={c.panel2} stroke={c.blueHi} strokeWidth={1.5} />} label="Step — produces an artifact" />
      <Key swatch={<rect x={2} y={5} width={14} height={9} rx={2} fill="none" stroke={c.line2} strokeWidth={1.5} strokeDasharray="2 2" />} label="Action step — no data/doc" />
      <Key swatch={<rect x={2} y={5} width={14} height={9} rx={2} fill="rgba(206,17,38,0.10)" stroke={c.redHi} strokeWidth={1.5} />} label="Critical-path step" />
    </div>
  );
}

// Static key — self-contained (no data), so it also drops straight into Help.
export function FlowLegend() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", alignItems: "center", font: `500 13px ${font.sans}`, color: c.muted2 }}>
      <Key swatch={<circle cx={9} cy={9} r={7} fill={c.panel} stroke={c.blueHi} strokeWidth={2} />} label="Phase" />
      <Key swatch={<circle cx={9} cy={9} r={7} fill={c.red} stroke="#fff" strokeWidth={2} />} label="Critical-path phase" />
      <Key swatch={<rect x={3} y={3} width={12} height={12} rx={2} transform="rotate(45 9 9)" fill={c.redHi} stroke="#fff" strokeWidth={1.5} />} label="⟐ Critical-path gate" />
      <Key swatch={<circle cx={9} cy={9} r={4} fill={c.green} />} label="Produces data" />
      <Key swatch={<circle cx={9} cy={9} r={4} fill={c.amber} />} label="Consumes data" />
    </div>
  );
}

function Key({ swatch, label }: { swatch: React.ReactNode; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <svg width={18} height={18}>{swatch}</svg>{label}
    </span>
  );
}

// Tiny static schematic for Help — three phases, one critical with a gate.
export function MiniFlowSchematic() {
  return (
    <svg viewBox="0 0 220 46" width="100%" style={{ maxWidth: 240, display: "block", marginTop: 4 }} aria-hidden>
      <line x1={20} y1={23} x2={200} y2={23} stroke={c.line2} strokeWidth={1.5} />
      <line x1={80} y1={23} x2={140} y2={23} stroke={c.red} strokeWidth={3} />
      <circle cx={20} cy={23} r={8} fill={c.panel} stroke={c.blueHi} strokeWidth={2} />
      <circle cx={80} cy={23} r={8} fill={c.red} stroke="#fff" strokeWidth={2} />
      <g transform="translate(140 23) rotate(45)"><rect x={-7} y={-7} width={14} height={14} rx={2} fill={c.redHi} stroke="#fff" strokeWidth={1.5} /></g>
      <circle cx={200} cy={23} r={8} fill={c.panel} stroke={c.blueHi} strokeWidth={2} />
      <text x={20} y={42} textAnchor="middle" fontSize={10.5} fontFamily={font.mono} fill={c.muted}>phase</text>
      <text x={80} y={42} textAnchor="middle" fontSize={10.5} fontFamily={font.mono} fill={c.redHi}>critical</text>
      <text x={140} y={42} textAnchor="middle" fontSize={10.5} fontFamily={font.mono} fill={c.redHi}>gate</text>
      <text x={200} y={42} textAnchor="middle" fontSize={10.5} fontFamily={font.mono} fill={c.muted}>phase</text>
    </svg>
  );
}

function badge(accent: string): React.CSSProperties {
  return { font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: accent, border: `1px solid ${accent}55`, borderRadius: 4, padding: "2px 5px" };
}
